import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export type Store = { id: string; name: string };

type DateRange = "7d" | "30d" | "90d" | "today" | "yesterday";

type AppFilters = {
  storeId: string | "all";
  setStoreId: (id: string | "all") => void;
  dateRange: DateRange;
  setDateRange: (r: DateRange) => void;
  stores: Store[];
  storesLoading: boolean;
  storesError: string | null;
  refreshKey: number;
  refresh: () => void;
  lastUpdated: Date;
};

const Ctx = createContext<AppFilters | null>(null);

export function AppFiltersProvider({ children }: { children: ReactNode }) {
  const [storeId, setStoreId] = useState<string | "all">("all");
  const [dateRange, setDateRange] = useState<DateRange>("30d");
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [stores, setStores] = useState<Store[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [storesError, setStoresError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStoresLoading(true);
      setStoresError(null);
      const { data, error } = await supabase
        .from("stores")
        .select("id, name")
        .eq("is_active", true)
        .order("name", { ascending: true });
      if (cancelled) return;
      if (error) {
        setStoresError(error.message);
        setStores([]);
      } else {
        setStores((data ?? []) as Store[]);
      }
      setStoresLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // If the currently selected store disappears (e.g. deactivated), fall back to "all".
  useEffect(() => {
    if (storeId !== "all" && !storesLoading && stores.length > 0 && !stores.some(s => s.id === storeId)) {
      setStoreId("all");
    }
  }, [storeId, stores, storesLoading]);

  const value = useMemo<AppFilters>(() => ({
    storeId, setStoreId,
    dateRange, setDateRange,
    stores,
    storesLoading,
    storesError,
    refreshKey,
    refresh: () => { setRefreshKey(k => k + 1); setLastUpdated(new Date()); },
    lastUpdated,
  }), [storeId, dateRange, stores, storesLoading, storesError, refreshKey, lastUpdated]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppFilters() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAppFilters must be used within AppFiltersProvider");
  return v;
}

export const dateRangeDays = (r: DateRange) => {
  switch (r) {
    case "today": return 1;
    case "yesterday": return 1;
    case "7d": return 7;
    case "30d": return 30;
    case "90d": return 90;
  }
};

export const dateRangeLabel = (r: DateRange) => ({
  today: "Today",
  yesterday: "Yesterday",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
} as const)[r];
